import type { ConversationCommitFiles } from '@crowdex/host-kit/renderer/commit-scope';
import {
  GitActionDialogs,
  type GitMetadataGenerator,
  useGitActions,
} from '@crowdex/host-kit/renderer/git-actions';
import type { NativeGitStatus } from '@crowdex/workspace-protocol';
import {
  ArrowUp,
  GitBranch,
  GitCommitHorizontal,
  GitPullRequest,
  LoaderCircle,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  SummaryActions,
  SummaryButtonRow,
  SummaryPill,
  SummaryRow,
  SummarySection,
} from '@/components/desktop-summary/summary-primitives';
import { SummaryPullRequests } from '@/components/desktop-summary/summary-pull-requests';
import {
  CreatePullRequestDialog,
  type CreatePullRequestInput,
} from '@/features/pull-request/create-pull-request-dialog';
import {
  type PullRequestSessionContext,
  useSessionPullRequest,
} from '@/features/pull-request/use-session-pull-request';
import { basename } from '@/lib/desktop-paths';
import { primaryCheckoutPath } from '@/lib/desktop-shell/execution-mode-controls';
import { useWorkspaceName } from '@/lib/desktop-shell/use-workspace-name';
import { formatCompactCount } from '@/lib/desktop-summary/format';

export { checksLabel } from '@/components/desktop-summary/summary-pull-requests';

const EMPTY_SANDBOX = /^\/workspace\/?$/;

/** A cloud sandbox holding no repository (its bare `/workspace`). */
function isEmptySandbox(root: string): boolean {
  return EMPTY_SANDBOX.test(root);
}

/**
 * The name of a conversation's folder in its chips: the repository's (`name`, the server's for
 * it), or "Empty sandbox" for a cloud sandbox holding none (its bare `/workspace`).
 */
export function repositoryLabel(root: string, name: string | null = null): string {
  return isEmptySandbox(root) ? 'Empty sandbox' : (name ?? basename(root));
}

/** `owner/name` of a forge remote (`git@github.com:o/n.git`, `https://host/o/n`), else null. */
export function repositoryFromRemote(remote: string | null | undefined): string | null {
  if (remote === null || remote === undefined) return null;
  const match = remote.trim().match(/[:/]([^/:]+\/[^/]+?)(?:\.git)?\/?$/);
  return match?.[1] ?? null;
}

/** What the Code section acts on: the checkout's git status and how to change it. */
export type SummaryGitControls = {
  /** The checkout's status on this computer; none in the cloud or before it is read. */
  gitStatus: NativeGitStatus | null;
  gitStatusState?: GitStatusState;
  /** The conversation runs in the cloud: its git is its agent's, not this computer's. */
  cloud: boolean;
  /** Creates the branch of a worktree that has none (the shell's Create branch dialog). */
  onCreateBranch?: (() => void) | undefined;
  onGenerateGitText?: GitMetadataGenerator | undefined;
  /** Re-reads the git status after a commit, a push, a pull request. */
  onGitStatusChange: () => Promise<void>;
  /** Settings › create pull requests as drafts. */
  draftPullRequests: boolean;
  /** The files the conversation's turns changed: what its commits take, unless told otherwise. */
  conversationFiles?: ConversationCommitFiles | null;
};

type GitStatusState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'unavailable'
  | 'not-repository'
  | 'folder-missing';

const STATUS_NOTICE: Partial<Record<GitStatusState, string>> = {
  loading: 'Loading Git status…',
  unavailable: 'Git unavailable',
  'not-repository': 'Not a Git repository',
  'folder-missing': 'Folder not on this device',
};

const noGeneration: GitMetadataGenerator = async () => {
  throw new Error('Generation is unavailable here.');
};

/**
 * The Code section's actions, as they apply: Create branch (a worktree without one), Commit…
 * (changes to commit) or Push (commits not pushed), Create PR (a branch without one). Commit and
 * push are the shared git dialogs; Create PR the pull request's own.
 */
function GitActionsRow({
  workspace,
  git,
  hasPullRequest,
  onCreated,
}: {
  workspace: string;
  git: SummaryGitControls & { gitStatus: NativeGitStatus };
  hasPullRequest: boolean;
  onCreated: () => void;
}) {
  const status = git.gitStatus;
  const branch = status.branch.trim();
  const actions = useGitActions({
    workspace,
    branch: status.branch,
    hasChanges: status.hasChanges,
    commitsAhead: status.commitsAhead,
    currentPullRequest: null,
    pullRequestUrl: status.pullRequestUrl ?? null,
    onGenerateGitText: git.onGenerateGitText ?? noGeneration,
    onGitStatusChange: git.onGitStatusChange,
    conversationFiles: git.conversationFiles ?? null,
  });
  const [creating, setCreating] = useState<CreatePullRequestInput | null>(null);
  const currentWorktree = status.worktrees.find((worktree) => worktree.isCurrent);
  const createBranch =
    branch.length === 0 && currentWorktree?.managed === true && git.onCreateBranch !== undefined;
  const commit = branch.length > 0 && status.hasChanges;
  const push = branch.length > 0 && actions.shouldPush;
  const availability = status.pullRequestAvailability;
  const createPullRequest =
    !hasPullRequest &&
    branch.length > 0 &&
    (availability === 'available' || availability === 'provider-unavailable');
  if (!createBranch && !commit && !push && !createPullRequest) return null;
  return (
    <>
      <SummaryActions label="Code actions">
        {createBranch && (
          <SummaryPill
            disabled={actions.working}
            icon={<GitBranch />}
            onClick={() => git.onCreateBranch?.()}
          >
            Create branch
          </SummaryPill>
        )}
        {commit && (
          <SummaryPill
            disabled={actions.working}
            icon={<GitCommitHorizontal />}
            onClick={actions.openCommitOrPush}
            title={
              git.conversationFiles
                ? "Commit this conversation's changes, and push them if you like"
                : 'Commit the changes, and push them if you like'
            }
          >
            Commit…
          </SummaryPill>
        )}
        {push && (
          <SummaryPill
            disabled={actions.working}
            icon={<ArrowUp />}
            onClick={actions.openCommitOrPush}
            title={`Push ${status.commitsAhead} ${status.commitsAhead === 1 ? 'commit' : 'commits'}`}
          >
            Push
          </SummaryPill>
        )}
        {createPullRequest && (
          <SummaryPill
            disabled={creating !== null || availability === 'provider-unavailable'}
            icon={<GitPullRequest />}
            onClick={() =>
              setCreating({
                workspace,
                branch: status.branch,
                branches: [...status.branches],
                hasChanges: status.hasChanges,
                draft: git.draftPullRequests,
                conversationFiles: git.conversationFiles ?? null,
              })
            }
            title={availability === 'provider-unavailable' ? 'Sign in to the provider' : undefined}
          >
            Create PR
          </SummaryPill>
        )}
      </SummaryActions>
      <GitActionDialogs
        actions={actions}
        branch={status.branch}
        existingBranches={status.branches}
        workspace={workspace}
      />
      {creating !== null && (
        <CreatePullRequestDialog
          generateText={git.onGenerateGitText}
          input={creating}
          onClose={(result) => {
            setCreating(null);
            if (result?.pullRequestUrl)
              window.open(result.pullRequestUrl, '_blank', 'noopener,noreferrer');
            if (result) onCreated();
          }}
          onRefresh={git.onGitStatusChange}
        />
      )}
    </>
  );
}

const NO_SESSION: PullRequestSessionContext = {
  taskId: '',
  workspace: null,
  cloud: false,
  visible: false,
  preview: false,
  gitStatus: null,
};

/**
 * The Summary's Code (D5): the repository, its branch (its chip opens the branch's actions:
 * switch, create), the changes of the conversation (they open Review), its pull request (it
 * opens the Pull request tool), then what can be done with them here: Commit…, Push, Create PR.
 * A conversation without a repository has none; an empty sandbox names itself and offers none.
 */
export function SummaryWorkspaceSection({
  workspace,
  remoteUrl,
  branch,
  branchControl,
  additions,
  deletions,
  onOpenChanges,
  onOpenPullRequest,
  pullRequest,
  git,
}: {
  workspace: string;
  remoteUrl: string | null;
  branch: string | null;
  /** The branch's chip with its actions (switch, create), shown in place of its name. */
  branchControl?: ReactNode;
  additions: number;
  deletions: number;
  onOpenChanges: () => void;
  onOpenPullRequest: () => void;
  /** What the pull request is read with; none in a test or before the git status. */
  pullRequest?: PullRequestSessionContext;
  /** Commit, push, create a pull request; none where git is not this computer's. */
  git?: SummaryGitControls;
}) {
  const emptySandbox = isEmptySandbox(workspace);
  const session = useSessionPullRequest(pullRequest ?? NO_SESSION, pullRequest !== undefined);
  const gitStatus = git === undefined || emptySandbox || git.cloud ? null : git.gitStatus;
  // A worktree is its repository's: named as the server names it, not by its own folder.
  const name = useWorkspaceName(workspace);
  const repository =
    repositoryFromRemote(remoteUrl) ??
    repositoryLabel(primaryCheckoutPath(gitStatus) ?? workspace, name);
  const notice =
    git === undefined || emptySandbox || git.cloud || git.gitStatus !== null
      ? undefined
      : STATUS_NOTICE[git.gitStatusState ?? 'idle'];
  return (
    <SummarySection card title="Code">
      <SummaryRow label="Repository">
        <span title={workspace}>{repository}</span>
      </SummaryRow>
      {branch !== null &&
        branch.length > 0 &&
        (branchControl !== undefined && !emptySandbox ? (
          // Its chip opens the branch's actions (switch, create): as wide as its name needs.
          <div className="type-ui flex min-w-0 items-center gap-3">
            <span className="shrink-0 text-muted-foreground">Branch</span>
            <span className="ml-auto flex min-w-0 justify-end">{branchControl}</span>
          </div>
        ) : (
          <SummaryRow label="Branch">
            <span className="font-mono text-xs" title={branch}>
              {branch}
            </span>
          </SummaryRow>
        ))}
      <button
        className="-mx-2 rounded-lg px-2 text-left transition-colors hover:bg-accent/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onOpenChanges}
        title="Review the changes"
        type="button"
      >
        <SummaryRow label="Changes">
          <span className="text-desktop-success tabular-nums">
            +{formatCompactCount(additions)}
          </span>{' '}
          <span className="text-destructive tabular-nums">−{formatCompactCount(deletions)}</span>
        </SummaryRow>
      </button>
      {pullRequest !== undefined && (
        <SummaryPullRequests
          cockpit={session.cockpit}
          conversationId={pullRequest.cloud || pullRequest.preview ? null : pullRequest.taskId}
          onOpenPrimary={onOpenPullRequest}
          primary={session.pullRequest}
          visible={pullRequest.visible}
        />
      )}
      {notice !== undefined && (
        <SummaryButtonRow
          icon={
            git?.gitStatusState === 'loading' ? (
              <LoaderCircle className="animate-spin" />
            ) : (
              <GitBranch />
            )
          }
        >
          <span className="text-muted-foreground">{notice}</span>
        </SummaryButtonRow>
      )}
      {git !== undefined && gitStatus !== null && (
        <GitActionsRow
          git={{ ...git, gitStatus }}
          hasPullRequest={session.pullRequest !== null}
          onCreated={session.reload}
          workspace={workspace}
        />
      )}
    </SummarySection>
  );
}
